<!--
  GoogleSignInButton.svelte — Custom-styled Google sign-in button.

  Uses the Google Identity Services (GSI) authorization-code flow
  (`google.accounts.oauth2.initCodeClient`) instead of `renderButton()`.
  This avoids the uncontrollable GSI iframe and gives full control
  over button appearance and sizing.

  On click the component opens a standard Google OAuth popup.  Once the
  user selects an account, the authorization code is dispatched via the
  `credential` event so the server can exchange it for tokens.

  Retries initialisation every 500ms for up to 5 seconds to handle
  the async GSI script loading.

  @prop {string} [text] — 'signin_with' | 'signup_with' | 'continue_with'.
-->
<script lang="ts">
	import { onDestroy } from 'svelte';
	import { Button } from 'flowbite-svelte';
	import { configurationStore } from '../AppConfiguration';
	import { t2 } from '../i18n';

	
	interface Props {
		/** Text variant shown on the button. */
		text?: 'signin_with' | 'signup_with' | 'continue_with';
		/** Callback fired with the Google authorization code string. */
		oncredential?: (_code: string) => void;
	}

	let { text = 'signin_with', oncredential }: Props = $props();

	/** GSI code-client type returned by `initCodeClient`. */
	type CodeClient = { requestCode: () => void };

	/** Whether the GSI code client has been successfully initialised. */
	let gsiReady = $state(false);

	/** Whether GSI failed to load after all retry attempts. */
	let gsiFailed = $state(false);

	/** Retry interval handle. */
	let retryInterval: ReturnType<typeof setInterval> | null = null;

	/** Number of retry attempts made. */
	let attempts = 0;

	/** Maximum retry attempts (5 seconds at 500ms intervals). */
	const MAX_ATTEMPTS = 10;

	/** The Google Client ID read from the server configuration. */
	let googleClientId: string | null = null;

	/** The initialised GSI code client instance. */
	let codeClient: CodeClient | null = null;

	/**
	 * Attempt to initialise the GSI authorization-code client.
	 * @returns true if successful, false if GSI is not yet available.
	 */
	function tryInitGSI(): boolean {
		const win = window as unknown as {
			google?: {
				accounts: {
					oauth2: {
						initCodeClient: (_config: Record<string, unknown>) => CodeClient;
					};
				};
			};
		};
		/** Bail out if the GSI library or client ID is not yet available. */
		if (!win.google?.accounts?.oauth2 || !googleClientId) return false;

		codeClient = win.google.accounts.oauth2.initCodeClient({
			client_id: googleClientId,
			scope: 'openid email profile',
			ux_mode: 'popup',
			callback: (response: { code?: string; error?: string }) => {
				if (response.code) {
					oncredential?.(response.code);
				}
			}
		});

		gsiReady = true;
		return true;
	}

	/**
	 * Handle the button click — open the Google OAuth popup.
	 */
	function handleClick(): void {
		if (codeClient) codeClient.requestCode();
	}

	/**
	 * Start the GSI retry loop that polls until the Google script
	 * is ready or the maximum number of attempts is reached.
	 */
	function startRetryLoop(): void {
		if (tryInitGSI()) return;

		/** Poll for GSI readiness at 500ms intervals until success or timeout. */
		retryInterval = setInterval(() => {
			attempts++;
			if (tryInitGSI() || attempts >= MAX_ATTEMPTS) {
				if (!gsiReady) gsiFailed = true;
				if (retryInterval) clearInterval(retryInterval);
				retryInterval = null;
			}
		}, 500);
	}

	/**
	 * React to configuration store changes.
	 * Once the config arrives, extract the googleClientId and begin
	 * GSI initialisation.  If the client ID is missing, mark as failed.
	 */
	const unsubscribe = configurationStore.subscribe((config) => {
		if (!config) return;

		googleClientId = config.googleClientId;

		/** Abort if no client ID is configured on the server. */
		if (!googleClientId) {
			console.error('Google Sign-In is unavailable: googleClientId is not configured on the server.');
			gsiFailed = true;
			return;
		}

		/* Only start if we haven't already initialised. */
		if (!gsiReady && !retryInterval) {
			startRetryLoop();
		}
	});

	onDestroy(() => {
		unsubscribe();
		if (retryInterval) clearInterval(retryInterval);
	});

	/** Human-readable label based on the text prop. */
	const labelMap: Record<string, string> = {
		signin_with: t2('Sign in with Google'),
		signup_with: t2('Sign up with Google'),
		continue_with: t2('Continue with Google')
	};
</script>

{#if $configurationStore?.googleClientId || !$configurationStore}
	<Button color="alternative" class="w-full" disabled={!gsiReady || gsiFailed} onclick={handleClick}>
		<svg class="mr-2" width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
			<path
				fill="#EA4335"
				d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
			/>
			<path
				fill="#4285F4"
				d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
			/>
			<path
				fill="#FBBC05"
				d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
			/>
			<path
				fill="#34A853"
				d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
			/>
		</svg>
		{labelMap[text] || t2('Sign in with Google')}
	</Button>
{/if}
